import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { isEmpty, uniqBy } from 'lodash';

import { getLuxuryHomeAvailability, ILuxuryHomeAvailability, ILuxuryHomeAvailabilityLoading, luxuryHomesSearchActions } from '@store/slices';
import { CondoLocationsEnum, CondoRequestTypeEnum } from '@common-types';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryParams } from '@utils';
import { ILuxuryHome } from '@share/common-types';
import { RootState } from '@share/utils';
import { Loading } from '@share/components';

import { LuxuryHomesPropertiesFilters } from '../../commons/property-filters/component';
import { LuxuryHomesDatesFilters } from '../../commons/dates-filters/component';
import { LuxuryHomesPropertyCard } from '../../commons/property-card/component';

import './style.scss';

export const LuxuryHomesProperties: React.FC = () => {

  const dispatch = useDispatch();
  
  const { placeId } = useParams<{ placeId: string }>();
  const { propertyId } = useParams<{ propertyId: string }>();

  const { homes } = useSelector((state: RootState) => state.luxuryHomesStore);
  
  const { checkIn, checkOut } = useQueryParams();

  const luxuryHomesSearchStore = useSelector((state: RootState) => state.luxuryHomesSearchStore);

  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedOcuppancy, setSelectedOcuppancy] = useState<string>('');
  const [selectedBedrooms, setSelectedBedrooms] = useState<string>('');
  const [selectedBathrooms, setSelectedBathrooms] = useState<string>('');
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [homesfiltered, setHomesfiltered] = useState(homes);
  const [dates, setDates] = useState<{
    checkIn: string | null | undefined;
    checkOut: string | null | undefined;
  } | null>(null);

  const filterHomes = (homesToFilter: ILuxuryHome[]) => {
    return homesToFilter?.filter((home: ILuxuryHome) => (!selectedLocation || home?.location?.id?.toString() === selectedLocation?.toString()))
                         .filter((home: ILuxuryHome) => (!selectedOcuppancy || home?.units?.map((unit: any) => unit?.maxOccupancy).some((maxOccupancy: number) => (maxOccupancy || 0) >= (selectedOcuppancy ? Number(selectedOcuppancy) : 0))))
                         .filter((home: ILuxuryHome) => (!selectedBedrooms || home?.units?.map((unit: any) => unit?.bedroomsCount).some((bedroomsCount: number) => (bedroomsCount || 0) >= (selectedBedrooms ? Number(selectedBedrooms) : 0))))
                         .filter((home: ILuxuryHome) => (!selectedBathrooms || home?.units?.map((unit: any) => unit?.bathroomsCount).some((bathroomsCount: number) => (bathroomsCount || 0) >= (selectedBathrooms ? Number(selectedBathrooms) : 0))))
                         .filter((home: ILuxuryHome) => (isEmpty(search) || home?.title?.toLowerCase().includes(search.toLowerCase())));
  }

  const initialHomes = useMemo(() => {
    let homesInPlace = homes?.filter((l: ILuxuryHome) => l.location.id?.toString() === placeId?.toString());
    if (propertyId) {
      homesInPlace = homesInPlace?.filter((l: ILuxuryHome) => l.id?.toString() === propertyId?.toString());
    }
  
    return homesInPlace;
  }, [homes]);
  
  useEffect(() => {
    setHomesfiltered(initialHomes);

    if (initialHomes?.length) {
      const startDate = checkIn ? checkIn : undefined;
      const endDate = checkOut ? checkOut : undefined;  
  
      if (startDate && endDate) {
        setDates({
          checkIn: startDate,
          checkOut: endDate,
        });
      }
    }
  }, [homes]);
  
  useEffect(() => {
    if (dates?.checkIn && dates?.checkOut) {
      if (initialHomes?.length) {
        uniqBy(initialHomes, 'id').map((h: any) =>
          dispatch(getLuxuryHomeAvailability(h?.id, {
            requestType: CondoRequestTypeEnum.Strict,
            adultsCount: 2,
            childrenCount: 0,
            bedroomsCount: 0,
            checkIn: dates?.checkIn ? dates?.checkIn : '',
            checkOut: dates?.checkOut ? dates?.checkOut : '',
            includeStudio: true,
            location: {
              geoLocation: {
                latitude  : h?.location?.location?.lat,
                longitude: h?.location?.location?.lng,
              },
              id: h?.location?.id?.toString(),
              locationType: CondoLocationsEnum.GenericCondo,
              name: h?.location?.name,
            }
          })));
      }
    } else {
      setHomesfiltered(initialHomes);
      dispatch(luxuryHomesSearchActions.setDefaultValues());
    }
  }, [dates]);

  useEffect(() => {
    const loading = luxuryHomesSearchStore.loading.some((l: ILuxuryHomeAvailabilityLoading) => l.loading);
    setLoading(loading);

    if (!loading && dates?.checkIn && dates?.checkOut && !luxuryHomesSearchStore.homes?.length) {
      setHomesfiltered([]);
    }
  }, [luxuryHomesSearchStore.loading]);

  useEffect(() => {
    if (dates?.checkIn && dates?.checkOut) {

      const roomIds = luxuryHomesSearchStore.homes.map((h: ILuxuryHomeAvailability) => h.home.units?.map((u: any) => u.roomId)).flat();
      const initialHomesFiltered = initialHomes.filter((h: any) => h.units?.some((u: any) => roomIds.includes(u.id)));

      setHomesfiltered(initialHomesFiltered);

    } else {
      setHomesfiltered(initialHomes);
    }
  }, [luxuryHomesSearchStore.homes]);

  const homesfilteredFinal = filterHomes(homesfiltered);

  return (
    <section className="luxury-place">
      <LuxuryHomesPropertiesFilters
        selectedLocation={selectedLocation}
        selectedOcuppancy={selectedOcuppancy}
        selectedBedrooms={selectedBedrooms}
        selectedBathrooms={selectedBathrooms}
        search={search}

        disabledLocations={loading}
        disabledSearch={loading}
        disabledOcuppancy={loading}
        disabledBedrooms={loading}
        disabledBathrooms={loading}

        setSelectedLocation={setSelectedLocation}
        setSelectedOcuppancy={setSelectedOcuppancy}
        setSelectedBedrooms={setSelectedBedrooms}
        setSelectedBathrooms={setSelectedBathrooms}
        setSearch={setSearch}

        hideLocations
      />

      <LuxuryHomesDatesFilters dates={dates} onDatesChange={setDates} disabled={loading} />

      {(loading) && (
        <div className="luxury-place__properties" style={{ minHeight: 350 }}>
          <Loading />
        </div>)}

      {(!loading && !homesfilteredFinal?.length) && (
        <div className="luxury-place__properties one-line">
          <div className="luxury-place__properties-empty">
            <h2>No properties found for the selected criteria</h2>
          </div>
        </div>)}

      {(!loading && !!homesfilteredFinal?.length) && (
        <div className="luxury-place__properties">
          {homesfilteredFinal?.map((home: ILuxuryHome, index: number) => (
            <LuxuryHomesPropertyCard
              key={`${index}-${dates?.checkIn || 'NA'}-${dates?.checkOut || 'NA'}`}
              home={home}

              locationId={placeId}
              dates={dates}
              guests={2}
            />))}
        </div>)}
    </section>
  );
};
